import { useState, useEffect } from 'react'
import { useParams, useSearchParams, useNavigate } from 'react-router-dom'
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { PhoneInput } from "@/components/ui/phone-input"
import { SSNInput } from "@/components/ui/ssn-input"
import { Label } from "@/components/ui/label"
import { Checkbox } from "@/components/ui/checkbox"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { AddressInputs } from '@/components/address-inputs'
import { PrescreenRequest } from '@prescreen/lib/types/prescreen'
import { usePrequalPasteHelper } from '@/hooks/use-prequal-paste-helper'
import { extractDate, yyyymmdd } from '@prescreen/lib/date-utils'
import { dollars, dollarsToNum } from '@prescreen/lib/formats'
import { apiFetch } from '@prescreen/lib/api-client'
import {
  BorrowerBrandingFrame,
  borrowerPageShellClass,
  borrowerSpinnerClass
} from '@/components/borrower/BorrowerBrandingFrame'

export default function PrequalificationPage() {
  const { requestId } = useParams<{ requestId: string }>()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const token = searchParams.get('token')

  const [formData, setFormData] = useState<Partial<PrescreenRequest> | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingData, setIsLoadingData] = useState(true)
  const [error, setError] = useState<string | null>(null)

  usePrequalPasteHelper(data => setFormData(prev => prev ? ({ ...prev, ...data }) : data))

  useEffect(() => {
    const loadPrescreenRequest = async () => {
      if (!requestId || !token) {
        setError('Missing required parameters')
        setIsLoadingData(false)
        return
      }

      try {
        const response = await apiFetch(`/api/prequalification/${requestId}?token=${token}`)
        const json = await response.json()

        if (json.success) {
          const prescreenRequest = json.data.prescreenRequest
          setFormData(prescreenRequest)

          if (prescreenRequest.status === 'completed') {
            navigate(`/prequalification/${requestId}/complete?token=${token}`, { replace: true })
            return
          }
          
          setIsLoadingData(false)
        } else {
          setError(json.error || 'Failed to load prequalification request')
          setIsLoadingData(false)
        }
      } catch (err) {
        setError('Network error. Please try again.')
        setIsLoadingData(false)
      }
    }

    loadPrescreenRequest()
  }, [requestId, token, navigate])

  const handleInputChange = (field: string, value: string | boolean | number | null) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.')
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...(prev?.[parent as keyof typeof prev] as any),
          [child]: value
        }
      } as Partial<PrescreenRequest>))
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      } as Partial<PrescreenRequest>))
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    setError(null)

    try {
      const response = await apiFetch('/api/prequalification/submit', {
        method: 'POST',
        body: JSON.stringify({
          requestId,
          token,
          formData: {
            ...formData,
            statedGrossAnnualIncome: dollarsToNum(formData?.statedGrossAnnualIncome),
          },
        }),
      })

      const data = await response.json()

      if (data.success) {
        if (data.data?.redirectUrl) {
          window.location.href = data.data.redirectUrl
        } else {
          window.location.href = `/prequalification/${requestId}/complete?token=${token}`
        }
      } else {
        if (response.status === 409 && data.data?.redirectUrl) {
          window.location.href = data.data.redirectUrl
        } else {
          setError(data.error || 'Failed to submit prequalification form')
        }
      }
    } catch (err) {
      setError('Network error. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  if (isLoadingData) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-2xl mx-auto px-4">
          <div className="text-center">
            <div className={`animate-spin rounded-full h-12 w-12 border-b-2 ${borrowerSpinnerClass(false)} mx-auto mb-4`}></div>
            <p className="text-gray-600">Loading prequalification request...</p>
          </div>
        </div>
      </div>
    )
  }

  if (error && !formData) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-2xl mx-auto px-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-red-600">Error Loading Prequalification Request</CardTitle>
            </CardHeader>
            <CardContent>
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
              <div className="mt-4">
                <Button
                  onClick={() => window.location.reload()}
                  variant="outline"
                >
                  Try Again
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    )
  }

  if (!formData) return null

  const branding = formData.branding
  const isBranded = branding !== undefined

  return (
    <BorrowerBrandingFrame branding={branding} className={borrowerPageShellClass(isBranded)}>
      <div className="max-w-2xl mx-auto px-4">
        {!isBranded && (
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Complete Your Credit Prequalification
            </h1>
            <p className="text-gray-600">
              Please provide the following information to complete your prequalification process
            </p>
          </div>
        )}
        {isBranded && (
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-foreground mb-2">
              Complete Your Credit Prequalification
            </h1>
            <p className="text-muted-foreground">
              Please provide the following information to complete your prequalification with{' '}
              {branding.displayName}
            </p>
          </div>
        )}

        <Card>
          <CardHeader>
            <CardTitle>Credit Prequalification Authorization</CardTitle>
            <CardDescription>
              This is a soft credit check that will not affect your credit score.
              Your information is secure and encrypted.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <div className="space-y-4">
                <div className="grid grid-cols-12 gap-4">
                  <div className="col-span-4">
                    <Label htmlFor="firstName">First Name *</Label>
                    <Input
                      id="firstName"
                      value={formData.firstName || ''}
                      onChange={(e) => handleInputChange('firstName', e.target.value)}
                      required
                    />
                  </div>
                  <div className="col-span-2">
                    <Label htmlFor="middleName">Middle</Label>
                    <Input
                      id="middleName"
                      value={formData.middleName || ''}
                      onChange={(e) => handleInputChange('middleName', e.target.value)}
                    />
                  </div>
                  <div className="col-span-4">
                    <Label htmlFor="lastName">Last Name *</Label>
                    <Input
                      id="lastName"
                      value={formData.lastName || ''}
                      onChange={(e) => handleInputChange('lastName', e.target.value)}
                      required
                    />
                  </div>
                  <div className="col-span-2">
                    <Label htmlFor="suffix">Suffix</Label>
                    <Input
                      id="suffix"
                      value={formData.suffix || ''}
                      onChange={(e) => handleInputChange('suffix', e.target.value)}
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="phoneNumber">Phone Number *</Label>
                  <PhoneInput
                    id="phoneNumber"
                    value={formData.phoneNumber || ''}
                    onChange={(value) => handleInputChange('phoneNumber', value)}
                    required
                  />
                </div>

                <div>
                  <Label>Email Address *</Label>
                  <Input
                    value={formData.userEmail || ''}
                    disabled={true}
                    required
                  />
                </div>

                <Label>Home Address</Label>
                <AddressInputs
                  address={formData.streetAddress}
                  onChange={streetAddress => setFormData({ ...formData, streetAddress })}
                />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Date of Birth *</Label>
                    <Input
                      type="date"
                      value={formData.birthDate ? yyyymmdd(extractDate(formData.birthDate)) : ''}
                      onChange={(e) => handleInputChange('birthDate', e.target.value)}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="ssn">Social Security Number *</Label>
                    <SSNInput
                      id="ssn"
                      value={formData.ssn || ''}
                      onChange={(value) => handleInputChange('ssn', value)}
                      required
                    />
                  </div>
                </div>

                <div>
                  <Label>Individual Annual Income (before taxes) *</Label>
                  <Input
                    value={formData.statedGrossAnnualIncome || ''}
                    onChange={(e) => {
                      const val = e.target.value?.trim().replace(/[^$0-9.,]/g, '')
                      handleInputChange('statedGrossAnnualIncome', val)
                    }}
                    onBlur={(e) => {
                      handleInputChange(
                        'statedGrossAnnualIncome',
                        dollars(formData.statedGrossAnnualIncome)
                      )
                    }}
                    placeholder="$0.00"
                    required
                  />
                </div>

                <div className="space-y-4">
                  <div className="flex items-start space-x-2">
                    <Checkbox
                      id="consent"
                      checked={formData.consent ?? false}
                      onCheckedChange={(checked) => handleInputChange('consent', checked as boolean)}
                      required
                    />
                    <div className="space-y-2">
                      <Label htmlFor="consent" className="text-sm font-medium">
                        I consent to the credit prequalification process *
                      </Label>
                      <p className="text-xs text-gray-600">
                        By checking this box, I authorize the soft credit check and confirm that:
                      </p>
                      <ul className="text-xs text-gray-600 list-disc list-inside space-y-1">
                        <li>I am providing accurate information</li>
                        <li>I understand this is a soft credit check that won't affect my credit score</li>
                        <li>I consent to the processing of my personal information for this prequalification</li>
                        <li>I understand my information will be securely transmitted and stored</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              <div className="pt-4">
                <Button
                  type="submit"
                  disabled={isLoading}
                  className="w-full"
                >
                  {isLoading ? 'Processing...' : 'Submit Prequalification Request'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        <div className={`mt-6 text-center text-sm ${isBranded ? 'text-muted-foreground' : 'text-gray-500'}`}>
          <p>Your information is encrypted and secure. This process typically takes 2-3 minutes.</p>
          {requestId && (
            <p className="mt-2 text-xs">Request ID: {requestId}</p>
          )}
        </div>
      </div>
    </BorrowerBrandingFrame>
  )
}

